/* eslint-disable @typescript-eslint/ban-types */

import styled from '@emotion/styled';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect } from 'react-router';

import { Modal } from 'src/shared/components/Modal';
import { BodyTextMedium, ButtonGroup, HeadingText40 } from 'src/shared/ds';
import { pxToRem } from 'src/shared/helpers/pxToRem';

import { IntakeFormTestID, Screens } from '../models/intakeForm';
import { getQuestionDataFor } from '../selectors/intakeFormSelectors';

interface Props {
    cancelAbandon: () => void;
    abandonedResponse: string | object;
    currentScreen: Screens;
    saveResponse: (
        questionId: string,
        response: string | string[],
        submitToCoach?: boolean,
    ) => Promise<void>;
    saveMultipleResponses: (responses: object, submitToCoach?: boolean) => Promise<void>;
}

const StyledModal = styled(Modal)({
    '.ant-modal-content': {
        boxShadow: '0 8px 16px 0 rgba(116, 135, 156, 0.5)',
        borderRadius: pxToRem(20),

        '.ant-modal-body': {
            padding: 0,
        },
    },
});

const Wrapper = styled('div')({
    padding: pxToRem(32),
    paddingTop: pxToRem(60),
});

export const IntakeFormSaveForLaterModal: React.FC<Props> = ({
    currentScreen,
    abandonedResponse,
    cancelAbandon,
    saveResponse,
    saveMultipleResponses,
}) => {
    const [confirmAbandon, setConfirmAbandon] = useState(false);
    const { t } = useTranslation();

    const [submitNowInProgress, setSubmitNowInProgress] = useState(false);
    const [saveForLaterInProgress, setSaveForLaterInProgress] = useState(false);

    const submitAndCloseModal = async (submitToCoach: boolean) => {
        if (typeof abandonedResponse === 'string') {
            const { questionId = '' } = getQuestionDataFor(currentScreen);

            await saveResponse(questionId as string, abandonedResponse, submitToCoach);
        } else {
            await saveMultipleResponses(abandonedResponse, submitToCoach);
        }

        setConfirmAbandon(true);
    };

    const handleSubmitNow = async () => {
        if (submitNowInProgress) {
            return;
        }

        setSubmitNowInProgress(true);

        await submitAndCloseModal(true);

        setSubmitNowInProgress(false);
    };

    const handleSaveForLater = async () => {
        if (saveForLaterInProgress) {
            return;
        }

        setSaveForLaterInProgress(true);

        await submitAndCloseModal(false);

        setSaveForLaterInProgress(false);
    };

    if (currentScreen === Screens.Screen1 && !abandonedResponse) {
        return <Redirect to="/dashboard" />;
    }

    if (confirmAbandon) {
        return <Redirect to="/dashboard" />;
    }

    return (
        <StyledModal visible footer={null} onCancel={cancelAbandon} centered>
            <Wrapper data-testid={IntakeFormTestID.AbandonModal}>
                <HeadingText40 centered>{t('intakeForms:abandon_modal.save_title')}</HeadingText40>
                <BodyTextMedium fontColor="darkGrey" marginTop={20} centered>
                    {t('intakeForms:abandon_modal.navigate_away_message')}
                </BodyTextMedium>
                <BodyTextMedium fontColor="darkGrey" marginTop={25} centered>
                    {t('intakeForms:abandon_modal.before_you_leave')}
                </BodyTextMedium>

                <ButtonGroup
                    marginTop={40}
                    firstButtonText={t('intakeForms:abandon_modal.submit_now')}
                    onFirstButtonClick={handleSubmitNow}
                    secondButtonText={t('intakeForms:abandon_modal.save_for_later')}
                    onSecondButtonClick={handleSaveForLater}
                    firstButtonLoading={submitNowInProgress}
                    secondButtonLoading={saveForLaterInProgress}
                />
            </Wrapper>
        </StyledModal>
    );
};
